import './TournamentModal.scss';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { authStore, tournamentStore } from '../../../../../platform/stores';
import { tournamentService } from '../../../../../platform/services';
import { TournamentStatus, TournamentType, TournamentWinCriteria } from '../../../../../platform/enums';

export const TournamentModal = observer(() => {
  const [activeTab, setActiveTab] = useState<'info' | 'leaderboard' | 'games'>('info');

  const tournament = tournamentStore.activeTournament;

  useEffect(() => {
    if (tournament && activeTab === 'games') {
      tournamentService.fetchTournamentGames(tournament.id);
    }
  }, [tournament, activeTab]);

  useEffect(() => {
    if (tournament && activeTab === 'leaderboard') {
      tournamentService.fetchTournamentLeaderboard(tournament.id);
    }
  }, [tournament, activeTab]);

  // Handle keyboard events (Escape key to close)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!tournament) {
    return null;
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate prize distribution
  const getPrizeDistribution = () => {
    if (!tournament.prizeBreakDowns || tournament.prizeBreakDowns.length === 0) {
      return [];
    }
    return tournament.prizeBreakDowns.slice(0, 3); // Get top 3 prizes
  };

  // Helper functions to convert enum values to string names
  const getTournamentTypeName = (type: TournamentType): string => {
    switch (type) {
      case TournamentType.Score:
        return 'Score';
      case TournamentType.Bet:
        return 'Bet';
      default:
        return 'Unknown';
    }
  };

  const getTournamentStatusName = (status: TournamentStatus): string => {
    switch (status) {
      case TournamentStatus.Draft:
        return 'Draft';
      case TournamentStatus.Pending:
        return 'Pending';
      case TournamentStatus.Finished:
        return 'Finished';
      case TournamentStatus.Cancelled:
        return 'Cancelled';
      case TournamentStatus.Frozen:
        return 'Frozen';
      default:
        return 'Unknown';
    }
  };

  const getTournamentWinCriteriaName = (winCriteria: TournamentWinCriteria): string => {
    switch (winCriteria) {
      case TournamentWinCriteria.Score:
        return 'Score';
      case TournamentWinCriteria.BiggestOdd:
        return 'Biggest Odd';
      case TournamentWinCriteria.WinTotalAmount:
        return 'Win Total Amount';
      case TournamentWinCriteria.WinCount:
        return 'Win Count';
      case TournamentWinCriteria.BetTotalAmount:
        return 'Bet Total Amount';
      case TournamentWinCriteria.BetCount:
        return 'Bet Count';
      default:
        return 'Unknown';
    }
  };

  // Get games from store
  const tournamentGames = tournamentStore.getTournamentGames(tournament.id);
  const loadingGames = tournamentStore.loadingGames;

  // Handle closing the modal
  const handleClose = () => {
    tournamentStore.setMainModalIsOpened(false);
  };

  return (
    <div className="tournament-modal">
      <div className="tournament-banner" style={{ backgroundImage: `url('${tournament.banner}')` }}></div>
      {/* Header */}
      <div className="tournament-header">
        <h1>{tournament.name.toUpperCase()}</h1>
        <div className="close-btn" onClick={handleClose} title="Close tournament modal">×</div>
      </div>

      {/* Tournament Information Section */}
      <div className="tournament-info-section-wrapper">
        <div className="tournament-info-section">
          <div className="tournament-overview">
            <div className="tournament-trophy">
              <div className="trophy-image" style={{ backgroundImage: `url('${tournament.thumbnail}')` }}></div>
            </div>

            <div className="tournament-details">
              {/* Prize Pool - Always show */}
              <div className="detail-item">
                <div className="detail-icon">🏆</div>
                <div className="detail-content">
                  <div className="detail-label">Prize Pool</div>
                  <div className="detail-value">{tournament.currency} {tournament.totalPrize}</div>
                </div>
              </div>

              {/* Type - Always show */}
              <div className="detail-item">
                <div className="detail-icon">⚙️</div>
                <div className="detail-content">
                  <div className="detail-label">Type</div>
                  <div className="detail-value">{getTournamentTypeName(tournament.type)}</div>
                </div>
              </div>

              {/* Entry Fee - Hide if 0 */}
              {tournament.entryFee > 0 && (
                <div className="detail-item">
                  <div className="detail-icon">💰</div>
                  <div className="detail-content">
                    <div className="detail-label">Entry Fee</div>
                    <div className="detail-value">{tournament.currency} {tournament.entryFee}</div>
                  </div>
                </div>
              )}

              {/* Start Time - Always show */}
              <div className="detail-item">
                <div className="detail-icon">📅</div>
                <div className="detail-content">
                  <div className="detail-label">Start Time</div>
                  <div className="detail-value">{formatDate(tournament.startedAt)}</div>
                </div>
              </div>

              {/* End Time - Always show */}
              <div className="detail-item">
                <div className="detail-icon">📅</div>
                <div className="detail-content">
                  <div className="detail-label">End Time</div>
                  <div className="detail-value">{formatDate(tournament.endedAt)}</div>
                </div>
              </div>

              {/* Min Players Count - Hide if 0 */}
              {tournament.minPlayerCount > 0 && (
                <div className="detail-item">
                  <div className="detail-icon">👤</div>
                  <div className="detail-content">
                    <div className="detail-label">Min Players Count</div>
                    <div className="detail-value">{tournament.minPlayerCount}</div>
                  </div>
                </div>
              )}

              {/* Status - Always show */}
              {/* <div className="detail-item">
                <div className="detail-icon">📊</div>
                <div className="detail-content">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">{getTournamentStatusName(tournament.status)}</div>
                </div>
              </div> */}

              {/* Win Criteria - Always show */}
              <div className="detail-item">
                <div className="detail-icon">🎯</div>
                <div className="detail-content">
                  <div className="detail-label">Win Criteria</div>
                  <div className="detail-value">{getTournamentWinCriteriaName(tournament.winCriteria)}</div>
                </div>
              </div>

              {/* Min Bet Amount - Hide if 0 */}
              {tournament.minBetAmount > 0 && (
                <div className="detail-item">
                  <div className="detail-icon">💎</div>
                  <div className="detail-content">
                    <div className="detail-label">Min Bet Amount</div>
                    <div className="detail-value">{tournament.currency} {tournament.minBetAmount}</div>
                  </div>
                </div>
              )}

              {/* Fixed Bet Amount - Hide if 0 */}
              {tournament.fixedBetAmount > 0 && (
                <div className="detail-item">
                  <div className="detail-icon">🎲</div>
                  <div className="detail-content">
                    <div className="detail-label">Fixed Bet Amount</div>
                    <div className="detail-value">{tournament.currency} {tournament.fixedBetAmount}</div>
                  </div>
                </div>
              )}

              {/* Max Buy Count - Hide if 0 */}
              {tournament.maxBuyCount > 0 && (
                <div className="detail-item">
                  <div className="detail-icon">🛒</div>
                  <div className="detail-content">
                    <div className="detail-label">Max Buy Count</div>
                    <div className="detail-value">{tournament.maxBuyCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tournament-nav">
        <div
          className={`nav-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          Info
        </div>
        <div
          className={`nav-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          Leaderboard
        </div>
        <div
          className={`nav-tab ${activeTab === 'games' ? 'active' : ''}`}
          onClick={() => setActiveTab('games')}
        >
          Games
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="tab-content info-content">
          <div className="prize-distribution">
            <h3>Prize Distribution</h3>

            {/* Top 3 Prizes */}
            <div className="top-prizes">
              {getPrizeDistribution().map((prize, index) => (
                <div key={index} className={`prize-card ${index === 0 ? 'first' : index === 1 ? 'second' : 'third'}`}>
                  <div className="prize-rank">#{prize.from}</div>
                  <div className="prize-amount">{tournament.currency} {prize.prize}</div>
                  <div className="prize-icon">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </div>
                </div>
              ))}
            </div>

            {/* Other Prizes */}
            <div className="other-prizes">
              {tournament.prizeBreakDowns && tournament.prizeBreakDowns.slice(3).map((prize, index) => (
                <div key={index + 3} className="prize-row">
                  <div className="prize-info">
                    <div className="prize-icon">🏆</div>
                    <div className="prize-range">#{prize.from} - {prize.to}</div>
                  </div>
                  <div className="prize-amount">{tournament.currency} {prize.prize}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tournament Rules */}
          <div className="tournament-rules">
            <h3>Tournament Rules</h3>
            <div className="rules-content">
              {tournament.rules && tournament.rules.map((rule, index) => (
                <p key={index}>{index + 1}. {rule}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div className="tab-content leaderboard-content">
          {/* Current Player Row */}
          {(() => {
            const currentUser = tournamentStore.getCurrentUserParticipant(tournament.id);

            return currentUser ? (
              <div className="current-player">
                <div className="player-info">
                  <div className="player-rank">#{currentUser.placement || 'N/A'} Me</div>
                  {/* <div className="player-name"></div> */}
                </div>
                <div className="player-stats">
                  <div className="stat-item">
                    <div className="stat-label">Score</div>
                    <div className="stat-value">{currentUser.score.toLocaleString()}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Prize</div>
                    <div
                      className="stat-value">{tournamentStore.calculatePrizeForPlacement(currentUser.placement)}</div>
                  </div>
                </div>
              </div>
            ) : (
              // <div className="current-player">
              //   <div className="player-info">#-- Not Participating</div>
              //   <div className="player-stats">
              //     <div className="stat-item">
              //       <div className="stat-label">Status</div>
              //       <div className="stat-value">Not Joined</div>
              //     </div>
              //   </div>
              // </div>
              <></>
            );
          })()}

          {/* Top 3 Players */}
          {(() => {
            const topPlayers = tournamentStore.getTopPlayers(tournament.id);
            const loadingLeaderboard = tournamentStore.loadingLeaderboard;

            if (loadingLeaderboard) {
              return (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading leaderboard...</p>
                </div>
              );
            }

            if (topPlayers.length === 0) {
              return <p>No participants found for this tournament.</p>;
            }

            return (
              <div className="top-players">
                {topPlayers.slice(0, 3).map((player, index) => {
                  const isCurrentUser = player.userId === authStore.userId;
                  return (
                    <div
                      key={index}
                      className={`top-player ${index === 0 ? 'first' : index === 1 ? 'second' : 'third'} ${isCurrentUser ? 'current-user' : ''}`}
                    >
                      <div className="player-rank">#{index + 1}</div>
                      <div
                        className="player-name">{isCurrentUser ? 'Me' : tournamentStore.formatUserId(player.userId)}</div>
                      <div className="player-score">{player.score.toLocaleString()} score</div>
                      <div
                        className="player-prize">{tournament.currency} {tournamentStore.calculatePrizeForPlacement(player.placement)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Remaining Players Table */}
          {(() => {
            const topPlayers = tournamentStore.getTopPlayers(tournament.id);
            const loadingLeaderboard = tournamentStore.loadingLeaderboard;

            if (loadingLeaderboard || topPlayers.length === 0) {
              return null;
            }

            // Show remaining players (positions 4+)
            const remainingPlayers = topPlayers.slice(3);

            if (remainingPlayers.length === 0) {
              return null;
            }

            return (
              <div className="players-table">
                <div className="table-header">
                  <div className="header-cell">Pos.</div>
                  <div className="header-cell">Player</div>
                  <div className="header-cell">Score</div>
                  <div className="header-cell">Placement</div>
                  <div className="header-cell">Prize</div>
                </div>
                {remainingPlayers.map((player, index) => {
                  const isCurrentUser = player.userId === authStore.userId;
                  const position = index + 4; // Position in the table (4+)

                  // Determine position-based class for current user
                  let positionClass = '';
                  if (isCurrentUser) {
                    if (position === 1) positionClass = 'first-place';
                    else if (position === 2) positionClass = 'second-place';
                    else if (position === 3) positionClass = 'third-place';
                  }

                  return (
                    <div
                      key={index}
                      className={`table-row ${isCurrentUser ? 'current-user' : ''} ${positionClass}`}
                    >
                      <div className="table-cell">#{position}</div>
                      <div
                        className="table-cell">{isCurrentUser ? 'Me' : tournamentStore.formatUserId(player.userId)}</div>
                      <div className="table-cell">{player.score.toLocaleString()}</div>
                      <div className="table-cell">#{player.placement}</div>
                      <div
                        className="table-cell">{tournament.currency} {tournamentStore.calculatePrizeForPlacement(player.placement)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Games Tab Content */}
      {activeTab === 'games' && (
        <div className="tab-content games-content">
          <div className="games-section">
            {loadingGames ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                {/* <p>Loading games...</p> */}
              </div>
            ) : tournamentGames.length === 0 ? (
              <p>No games found for this tournament.</p>
            ) : (
              <div className="games-grid">
                {tournamentGames.map((game, index) => (
                  <div key={game.id} className="game-card">
                    <div className="game-image">
                      <div
                        className="game-artwork"
                        style={{ backgroundImage: `url('${game.image}')` }}
                      ></div>
                    </div>
                    <div className="game-title">{game.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});